
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  Calculator,
  Briefcase,
  ShoppingBag,
  BarChart3,
  Users,
  UserCog,
  Handshake,
  BookmarkCheck,
  Settings,
} from "lucide-react";
import { cn } from "@tsi/ui";
import { escondidaNaV1 } from "@/shared/config/navegacao";
import { usePermissoes } from "@/features/auth";

export const NAV_ITEMS = [
  { href: "/painel", label: "Painel", icon: LayoutDashboard, group: "Geral" },
  { href: "/estoque", label: "Estoque", icon: Boxes, group: "Geral" },
  { href: "/clientes", label: "Clientes", icon: Users, group: "Geral" },
  { href: "/simulador", label: "Simulador", icon: Calculator, group: "Geral" },
  { href: "/vendas", label: "Vendas", icon: Briefcase, group: "Pipeline" },
  { href: "/compras", label: "Compras", icon: ShoppingBag, group: "Pipeline" },
  { href: "/analises", label: "Análises", icon: BarChart3, group: "Insights" },
  { href: "/reservas", label: "Reservas", icon: BookmarkCheck, group: "TSI Parceiros" },
  { href: "/parceiros", label: "Parceiros", icon: Handshake, group: "TSI Parceiros" },
  { href: "/usuarios", label: "Usuários", icon: UserCog, group: "Acesso" },
  { href: "/configuracoes", label: "Configurações", icon: Settings, group: "Acesso" },
] as const;

const ITENS_VISIVEIS = NAV_ITEMS.filter((i) => !escondidaNaV1(i.href));

interface NavigationProps {
  onNavigate?: () => void;
}

export function Navigation({ onNavigate }: NavigationProps) {
  const { pathname } = useLocation();
  const { podeVerRota } = usePermissoes();
  const itens = ITENS_VISIVEIS.filter((i) => podeVerRota(i.href));
  const groups = Array.from(new Set(itens.map((i) => i.group)));

  return (
    <nav className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group} className="flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
            {group}
          </div>
          {itens.filter((i) => i.group === group).map((item) => {
            const Icon = item.icon;
            const active = pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  "group relative flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-all",
                  active
                    ? "bg-card text-foreground shadow-card"
                    : "text-muted-foreground hover:bg-card/60 hover:text-foreground"
                )}
              >
                {active && (
                  <span className="absolute -left-[14px] top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-brand" />
                )}
                <Icon
                  className={cn(
                    "h-4 w-4 shrink-0 transition-colors",
                    active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
                  )}
                />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
